import { WORLD } from '@blorbs/common';
import type { Vec } from '@blorbs/common';

/**
 * The whole arena is always on screen — this is a party game, everyone
 * watches everyone. The camera fits the island plus a lava margin, so as the
 * island shrinks the view zooms in for drama and legibility.
 */
export class Camera {
  width = 1;
  height = 1;
  dpr = 1;
  scale = 1;
  /** screen-space shake offset in px */
  shakeX = 0;
  shakeY = 0;

  private targetView = 1200;
  private view = 1200;

  resize(width: number, height: number, dpr: number): void {
    this.width = width;
    this.height = height;
    this.dpr = dpr;
    this.applyScale();
  }

  /** Called every frame with the island radius; eases the zoom. */
  track(islandRadius: number, dt: number): void {
    this.targetView = Math.max(2 * (islandRadius + 150), 700);
    const k = 1 - Math.exp(-dt * 2.5);
    this.view += (this.targetView - this.view) * k;
    this.applyScale();
  }

  private applyScale(): void {
    this.scale = Math.min(this.width, this.height) / this.view;
  }

  /** Apply the world transform (call after clearing the canvas). */
  apply(ctx: CanvasRenderingContext2D): void {
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.translate(this.width / 2 + this.shakeX, this.height / 2 + this.shakeY);
    ctx.scale(this.scale, this.scale);
    ctx.translate(-WORLD.cx, -WORLD.cy);
  }

  /** Reset to screen space (CSS px). */
  screen(ctx: CanvasRenderingContext2D): void {
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  toScreen(x: number, y: number): Vec {
    return {
      x: (x - WORLD.cx) * this.scale + this.width / 2 + this.shakeX,
      y: (y - WORLD.cy) * this.scale + this.height / 2 + this.shakeY,
    };
  }

  toWorld(sx: number, sy: number): Vec {
    return {
      x: (sx - this.width / 2 - this.shakeX) / this.scale + WORLD.cx,
      y: (sy - this.height / 2 - this.shakeY) / this.scale + WORLD.cy,
    };
  }

  /** Visible world rect (for culling / filling the lava). */
  worldBounds(): { x0: number; y0: number; x1: number; y1: number } {
    const a = this.toWorld(0, 0);
    const b = this.toWorld(this.width, this.height);
    return { x0: a.x, y0: a.y, x1: b.x, y1: b.y };
  }
}
